/**
 * Draft Shift API Contract
 *
 * Manages draft shifts for schedule editing workflow.
 * Drafts are temporary shift changes that can be published or reverted.
 *
 * SECURITY NOTES:
 * - companyId comes from JWT context, NOT from request input
 * - Batch operations (publish/revert) require permission on ALL affected locations
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { CreateDraftShiftInputSchema, GetDraftsQuerySchema, PublishDraftsInputSchema, RevertDraftsInputSchema, RevertDraftsByLocationInputSchema, DraftShiftResultSchema, PublishDraftsResponseSchema, RevertDraftsResponseSchema, GetDraftForShiftResponseSchema, GetDraftsResponseSchema, } from "@hourly/api/draftShift";
// ============ Contract Definitions ============
const createDraftShift = oc
    .route({
    method: "POST",
    path: "/draft-shifts",
    summary: "Create or update a draft shift",
    tags: ["Draft Shifts"],
    successStatus: 201,
})
    .input(CreateDraftShiftInputSchema.omit({ companyId: true }))
    .output(DraftShiftResultSchema);
const getDraftShifts = oc
    .route({
    method: "GET",
    path: "/draft-shifts",
    summary: "List draft shifts for the current company",
    tags: ["Draft Shifts"],
})
    .input(GetDraftsQuerySchema.omit({ companyId: true }))
    .output(GetDraftsResponseSchema);
const getDraftForShift = oc
    .route({
    method: "GET",
    path: "/draft-shifts/for-shift/{shiftId}",
    summary: "Get draft for a specific shift",
    tags: ["Draft Shifts"],
})
    .input(z.object({ shiftId: z.uuid() }))
    .output(GetDraftForShiftResponseSchema);
const publishDrafts = oc
    .route({
    method: "POST",
    path: "/draft-shifts/publish",
    summary: "Publish multiple draft shifts",
    tags: ["Draft Shifts"],
})
    .input(PublishDraftsInputSchema)
    .output(PublishDraftsResponseSchema);
const revertDrafts = oc
    .route({
    method: "POST",
    path: "/draft-shifts/revert",
    summary: "Revert multiple draft shifts",
    tags: ["Draft Shifts"],
})
    .input(RevertDraftsInputSchema)
    .output(RevertDraftsResponseSchema);
const revertDraftsByLocation = oc
    .route({
    method: "POST",
    path: "/draft-shifts/revert-by-location",
    summary: "Revert all drafts for a location within a date range",
    tags: ["Draft Shifts"],
})
    .input(RevertDraftsByLocationInputSchema)
    .output(RevertDraftsResponseSchema);
// ============ Export Contract ============
export const draftShiftContract = {
    create: createDraftShift,
    list: getDraftShifts,
    getForShift: getDraftForShift,
    publish: publishDrafts,
    revert: revertDrafts,
    revertByLocation: revertDraftsByLocation,
};
//# sourceMappingURL=draftShift.js.map